import { queryOptions, useQuery } from '@tanstack/react-query';
import { gql, type Variables } from 'graphql-request';

import { getQueryClient } from '@/api/queryClient';
import { COUNTRY_COOKIE } from '@/app/consts/cookies';
import logToDatadog from '@/logger/logToDatadog';
import { getCookie } from '@/utils/cookie';
import gqlRequest from '@/utils/gqlRequest';

import type {
  SellToCountry,
  SellToCountry_content_countries,
  SellToCountryVariables,
} from './types/SellToCountry';

export const query = gql`
  query SellToCountry($countryCode: String) {
    content {
      countries(countryCode: $countryCode) {
        currency
        countryCode
        eu
      }
    }
  }
`;

export const FALLBACK_SELL_TO_COUNTRY: SellToCountry_content_countries = {
  countryCode: 'US',
  currency: 'USD',
  eu: false,
};

export const sellToCountryOptions = (vars: SellToCountryVariables) => {
  const queryKey = ['SellToCountry', { vars }];

  return queryOptions({
    queryKey,
    queryFn: async (): Promise<SellToCountry_content_countries> => {
      try {
        const data = await gqlRequest<SellToCountry>(
          query,
          vars as unknown as Variables,
        );

        const countries = (data.content?.countries ?? []).filter((c) =>
          Boolean(c),
        );

        if (countries.length !== 1 || !countries[0]) {
          logToDatadog({
            data: {
              ...data.content,
              vars,
            },
            level: 'error',
            message: `Expected exactly one country for country code ${vars.countryCode}, but got ${countries.length}`,
            module: 'countries',
          });
        } else {
          return countries[0];
        }
      } catch (error) {
        logToDatadog({
          data: {
            error,
            vars,
          },
          level: 'error',
          message: `SellToCountry GraphQL query failed for country code ${vars.countryCode}`,
          module: 'countries',
        });
      }

      // In the event that no country is found, fallback to US after logging error(s).
      return FALLBACK_SELL_TO_COUNTRY;
    },
    initialData: () => {
      const queryClient = getQueryClient();
      return queryClient.getQueryData<
        SellToCountry_content_countries,
        typeof queryKey
      >(queryKey);
    },
  });
};

export function useSellToCountry(countryCode?: string) {
  const countryCodeCookie = getCookie(COUNTRY_COOKIE);
  const _countryCode = countryCode ?? countryCodeCookie ?? 'US';
  return useQuery(sellToCountryOptions({ countryCode: _countryCode }));
}
